import React, { useCallback, useId, useRef, useState } from "react";
import {
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

import { SeccionFormulario } from "../../../components/base";
import { getJefesListAsync } from "../../../api/Users";
import { ROL_SIN_JEFE, useCargaLocal } from "../comunes";

export const LARGO_MINIMO_CLAVE = 6;

export const DATOS_VACIOS = {
  firstName: "",
  secondName: "",
  lastName: "",
  secondLastName: "",
  phone: "",
  address: "",
  userName: "",
  passWord: "",
  confirmPassword: "",
  selectedRol: "",
  selectedJefe: "",
};

const vacio = (valor) => String(valor ?? "").trim().length === 0;

/**
 * Valida el formulario de usuario con las mismas reglas que exige el servidor (la dirección es
 * obligatoria en AddUser y UpdateUser) y las que ya pedía la web.
 * @param {object} datos
 * @param {boolean} esAlta En el alta se piden además el usuario y la contraseña.
 * @param {Array} [roles] Roles activos: si el rol elegido no está entre ellos (un rol desactivado
 *   que tenía guardado), hay que elegir otro.
 * @returns {Object<string, string>} errores por campo; vacío si todo está bien.
 */
export const validarUsuario = (datos, esAlta, roles) => {
  const errores = {};
  if (roles && datos.selectedRol !== "" && !roles.some((r) => r.id === datos.selectedRol)) {
    errores.selectedRol = "Su rol actual está desactivado: elija otro";
  }
  if (vacio(datos.firstName)) errores.firstName = "Ingrese el primer nombre";
  if (vacio(datos.lastName)) errores.lastName = "Ingrese el primer apellido";
  if (vacio(datos.phone)) errores.phone = "Ingrese el teléfono";
  if (vacio(datos.address)) errores.address = "Ingrese la dirección";
  if (datos.selectedRol === "") errores.selectedRol = "Seleccione un rol";
  if (datos.selectedJefe === "" && datos.selectedRol !== "" && datos.selectedRol !== ROL_SIN_JEFE) {
    errores.selectedJefe = "Seleccione el jefe inmediato";
  }
  if (esAlta) {
    if (vacio(datos.userName)) errores.userName = "Ingrese el usuario de acceso";
    if (vacio(datos.passWord)) {
      errores.passWord = "Ingrese una contraseña";
    } else if (datos.passWord.length < LARGO_MINIMO_CLAVE) {
      errores.passWord = `La contraseña debe tener al menos ${LARGO_MINIMO_CLAVE} caracteres`;
    }
    if (vacio(datos.confirmPassword)) {
      errores.confirmPassword = "Confirme la contraseña";
    } else if (datos.passWord !== datos.confirmPassword) {
      errores.confirmPassword = "Las contraseñas no coinciden";
    }
  }
  return errores;
};

/** Lo que recibe el servidor: sin jefe (Gerencia General) va el id del rol, como siempre. */
export const datosParaServidor = (datos) => ({
  firstName: datos.firstName,
  secondName: datos.secondName,
  lastName: datos.lastName,
  secondLastName: datos.secondLastName,
  phone: datos.phone,
  address: datos.address,
  supervisorId: datos.selectedJefe === "" ? datos.selectedRol : datos.selectedJefe,
  idRole: datos.selectedRol,
});

/**
 * Jefes posibles para un rol (usuarios de un nivel jerárquico más alto). Carga local: mientras
 * llegan, el selector dice "Cargando…"; si fallan, lo dice junto al campo.
 */
export const useJefes = (token) => {
  const rol = useRef(null);
  const [jefes, setJefes] = useState([]);
  const { cargando, error, cargar } = useCargaLocal(() => getJefesListAsync(token, rol.current));

  const cargarJefes = useCallback(
    async (idRol) => {
      setJefes([]);
      rol.current = idRol;
      if (idRol === "" || idRol === null || idRol === undefined) return;
      const result = await cargar();
      // Si mientras tanto se volvió a "Seleccione un rol", esta lista ya no corresponde.
      if (result && rol.current === idRol) setJefes(result.data.result);
    },
    [cargar]
  );

  return { jefes, cargandoJefes: cargando && rol.current !== null, errorJefes: error, cargarJefes };
};

/**
 * Campos del usuario, compartidos por el alta y la edición.
 * @param {object} props
 * @param {object} props.datos Valores (ver DATOS_VACIOS).
 * @param {Function} props.onCampo (campo, valor) => void
 * @param {Function} props.onRol Cambio de rol: el padre limpia el jefe y carga la lista nueva.
 * @param {object} props.errores
 * @param {boolean} props.esAlta
 * @param {boolean} props.deshabilitado
 * @param {Array} props.roles
 * @param {{jefes: Array, cargandoJefes: boolean, errorJefes: any}} props.estadoJefes
 * @param {string} [props.rolActual] Nombre del rol que tiene guardado (edición), por si ya no
 *   está activo y no aparece en la lista.
 */
const FormularioUsuario = ({
  datos,
  onCampo,
  onRol,
  errores,
  esAlta,
  deshabilitado,
  roles,
  estadoJefes,
  rolActual,
}) => {
  const prefijo = useId();
  const [verClave, setVerClave] = useState(false);
  const { jefes, cargandoJefes, errorJefes } = estadoJefes;
  const sinJefe = datos.selectedRol === ROL_SIN_JEFE;
  // Un rol desactivado no viene en la lista de roles: el selector se vería vacío sin decir nada.
  const rolEnLista = roles.some((r) => r.id === datos.selectedRol);
  const rolDesactivado = datos.selectedRol !== "" && !rolEnLista;

  const texto = (campo, etiqueta, transformar = (v) => v, extra = {}) => (
    <TextField
      fullWidth
      id={`${prefijo}-${campo}`}
      label={etiqueta}
      value={datos[campo] ?? ""}
      onChange={(e) => onCampo(campo, transformar(e.target.value))}
      error={Boolean(errores[campo])}
      helperText={errores[campo] || extra.ayuda || " "}
      disabled={deshabilitado}
      inputProps={{ autoComplete: "off", ...extra.inputProps }}
      {...extra.props}
    />
  );

  const mayusculas = (v) => v.toUpperCase();

  const botonVerClave = (
    <InputAdornment position="end">
      <IconButton
        onClick={() => setVerClave(!verClave)}
        aria-label={verClave ? "Ocultar contraseña" : "Mostrar contraseña"}
        edge="end"
      >
        {verClave ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
      </IconButton>
    </InputAdornment>
  );

  // El jefe guardado puede no estar en la lista (p. ej. el id del rol en Gerencia General, o un
  // jefe que ya no tiene un nivel superior): sin opción que coincida el selector queda vacío, en
  // vez de mostrar un valor que no existe, y se avisa que se conserva.
  const jefeEnLista = jefes.some((j) => j.id === datos.selectedJefe);
  const jefeFueraDeLista =
    !sinJefe && datos.selectedJefe !== "" && !jefeEnLista && !cargandoJefes && !errorJefes;

  let ayudaJefe = " ";
  if (sinJefe) ayudaJefe = "Gerencia General no tiene jefe inmediato";
  else if (datos.selectedRol === "") ayudaJefe = "Primero seleccione el rol";
  else if (cargandoJefes) ayudaJefe = "Cargando jefes…";
  else if (errorJefes) ayudaJefe = "No se pudieron cargar los jefes. Vuelva a elegir el rol.";
  else if (jefeFueraDeLista)
    ayudaJefe = "El jefe guardado no está entre los disponibles para este rol; se conserva si no elige otro";
  else if (jefes.length === 0) ayudaJefe = "No hay usuarios de un nivel superior a este rol";

  return (
    <>
      <SeccionFormulario titulo="Datos personales">
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            {texto("firstName", "Primer nombre", mayusculas, { props: { required: true } })}
          </Grid>
          <Grid item xs={12} md={6}>
            {texto("secondName", "Segundo nombre", mayusculas)}
          </Grid>
          <Grid item xs={12} md={6}>
            {texto("lastName", "Primer apellido", mayusculas, { props: { required: true } })}
          </Grid>
          <Grid item xs={12} md={6}>
            {texto("secondLastName", "Segundo apellido", mayusculas)}
          </Grid>
          <Grid item xs={12} md={6}>
            {texto("phone", "Teléfono", mayusculas, {
              props: { required: true, type: "tel" },
              inputProps: { inputMode: "tel" },
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {texto("address", "Dirección", mayusculas, { props: { required: true } })}
          </Grid>
        </Grid>
      </SeccionFormulario>

      <SeccionFormulario titulo="Acceso al sistema">
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            {esAlta
              ? texto("userName", "Usuario", (v) => v.toLowerCase(), {
                  ayuda: "Con este nombre entra al sistema y a la app",
                  props: { required: true },
                  inputProps: { autoCapitalize: "none" },
                })
              : texto("userName", "Usuario", (v) => v, {
                  ayuda: "El usuario de acceso no se puede cambiar",
                  props: { InputProps: { readOnly: true } },
                })}
          </Grid>
          <Grid item xs={12} md={6} />
          {esAlta ? (
            <>
              <Grid item xs={12} md={6}>
                {texto("passWord", "Contraseña", (v) => v, {
                  ayuda: `Al menos ${LARGO_MINIMO_CLAVE} caracteres`,
                  props: {
                    required: true,
                    type: verClave ? "text" : "password",
                    InputProps: { endAdornment: botonVerClave },
                  },
                  inputProps: { autoComplete: "new-password" },
                })}
              </Grid>
              <Grid item xs={12} md={6}>
                {texto("confirmPassword", "Confirmar contraseña", (v) => v, {
                  props: {
                    required: true,
                    type: verClave ? "text" : "password",
                    InputProps: { endAdornment: botonVerClave },
                  },
                  inputProps: { autoComplete: "new-password" },
                })}
              </Grid>
            </>
          ) : null}
          <Grid item xs={12} md={6}>
            <TextField
              select
              required
              fullWidth
              id={`${prefijo}-selectedRol`}
              label="Rol"
              value={rolEnLista ? datos.selectedRol : ""}
              onChange={(e) => onRol(e.target.value)}
              error={Boolean(errores.selectedRol) || rolDesactivado}
              helperText={
                rolDesactivado
                  ? `Su rol actual${rolActual ? ` (${rolActual})` : ""} está desactivado: elija otro`
                  : errores.selectedRol || "Define qué puede ver y hacer en el sistema"
              }
              disabled={deshabilitado}
            >
              <MenuItem value="">
                <em>Seleccione un rol</em>
              </MenuItem>
              {roles.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.rolName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              required={!sinJefe}
              fullWidth
              id={`${prefijo}-selectedJefe`}
              label="Jefe inmediato"
              value={jefeEnLista ? datos.selectedJefe : ""}
              onChange={(e) => onCampo("selectedJefe", e.target.value)}
              error={Boolean(errores.selectedJefe)}
              helperText={errores.selectedJefe || ayudaJefe}
              disabled={deshabilitado || sinJefe || datos.selectedRol === "" || cargandoJefes}
            >
              <MenuItem value="">
                <em>{sinJefe ? "Sin jefe inmediato" : "Seleccione el jefe inmediato"}</em>
              </MenuItem>
              {jefes.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.fullName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </SeccionFormulario>
    </>
  );
};

export default FormularioUsuario;
